import { v4 as uuid4 } from 'uuid';
import { APP_TO_WEB, WEB_TO_APP, sendApplicationEventByBridge } from '@constant/common/bridge-event';

export interface INativePhoto {
  fileId: string;
  filename: string;
  contentType: string;
  filesize: number;
  width?: number;
  height?: number;
}

export interface INativePhotoResult {
  eventId: string;
  status: 'success' | 'cancelled' | 'denied' | 'blocked' | 'busy' | 'error';
  photo?: INativePhoto;
  error?: string;
}

// 수신 준비 후 요청한다. eventId가 같은 응답만 받아 다른 사진 요청과 섞이지 않게 한다.
const requestPhoto = (
  type: string,
  resultEvent: string,
  data: Record<string, string>,
  timeout: number,
): Promise<INativePhotoResult> => {
  if (!window.ReactNativeWebView) return Promise.reject(new Error('앱에서 사용할 수 있는 사진 기능입니다.'));
  const eventId = uuid4();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener(resultEvent, handleResult);
    };
    const handleResult = (event: Event) => {
      const result = (event as CustomEvent<INativePhotoResult>).detail;
      if (result?.eventId !== eventId) return;
      cleanup();
      resolve(result);
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('사진 처리 응답이 없습니다. 다시 시도해주세요.'));
    }, timeout);
    window.addEventListener(resultEvent, handleResult);
    try {
      sendApplicationEventByBridge(type, { ...data, eventId });
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
};

// 한 번에 한 장을 바로 촬영한다. 취소는 오류가 아닌 cancelled 결과로 반환한다.
export const takeNativePhoto = () => requestPhoto(WEB_TO_APP.TAKE_PHOTO, APP_TO_WEB.TAKE_PHOTO_RESULT, {}, 300000);

// 사진 보관함에서 한 장을 선택한다.
export const selectNativePhoto = () =>
  requestPhoto(WEB_TO_APP.SELECT_PHOTO, APP_TO_WEB.SELECT_PHOTO_RESULT, {}, 300000);

// 서버가 발급한 URL과 선택 당시 contentType을 전달한다. 서버 upload-check는 호출부 책임이다.
export const uploadNativePhoto = (photo: INativePhoto, uploadUrl: string) =>
  requestPhoto(
    WEB_TO_APP.UPLOAD_PHOTO,
    APP_TO_WEB.UPLOAD_PHOTO_RESULT,
    { fileId: photo.fileId, uploadUrl, contentType: photo.contentType },
    150000,
  );

// 첨부 취소 또는 서버의 업로드 확인 완료 후 RN 캐시 사본을 해제한다.
export const removeNativePhoto = (fileId: string) =>
  requestPhoto(WEB_TO_APP.REMOVE_PHOTO, APP_TO_WEB.REMOVE_PHOTO_RESULT, { fileId }, 30000);
